import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import AddToCartButton from '@src/components/AddToCartButton'
import {
  useGlobalSnackbar,
  SnackbarMessages,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { determineCatalogType } from '@src/utils/searchUtils'
import CatalogImage from '@src/components/CatalogImage'
import { useRouter } from '@src/routes'
import { usePricingAndAvailabilityQuery } from '@src/queries/PricingAndAvailabilityQuery.generated'
import { CartRequestAction, CartItemType } from '@src/types/graphql-types'
import { useAddToCart } from '@utils/useCart'
import EmprovePricing from './EmprovePricing'
import { DossierSubType } from './DocumentAddToCart'
import { OriginalError, extractData } from '@utils/errorHandler'
import { setEmproveAddToCartData } from '@utils/analytics/cartAndCheckoutEvents'
import { AddToCartPagesEnum } from '@utils/analytics/enums'
import { QUICK_CART } from '@src/queries/QuickCartQuery'
import { useOptlyAddToCart } from '@src/utils/analytics/OptimizelyEvents/useAddToCartEvent'

const useStyles = makeStyles((theme: Theme) => {
  return {
    documentPurchase: {
      flexWrap: 'nowrap',
    },
    imageContainer: {
      height: theme.typography.pxToRem(110),
      width: theme.typography.pxToRem(110),
      position: 'relative',
    },
    defaultImage: {
      borderRadius: theme.typography.pxToRem(8),
      maxHeight: '100%',
    },
    purchaseTerms: {
      fontSize: theme.typography.pxToRem(14),
    },
    cartDetails: {
      lineHeight: theme.typography.pxToRem(50),
      fontSize: theme.typography.pxToRem(16),
      paddingTop: theme.spacing(3),
    },
    documentDetails: {
      paddingLeft: theme.spacing(5),
      fontSize: theme.typography.pxToRem(16),
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    formControl: {
      margin: theme.spacing(1, 0),
      width: '100%',
    },
  }
})

enum EmproveSubscriptions {
  ONE_YEAR = 'Y700000008',
  TWO_YEAR = 'Y700000003',
  FIVE_YEAR = 'Y700000009',
}

const subscriptionOptions = [
  {
    term: 1,
    name: {
      id: 'EMPROVE_SUBSCRIPTION_ONE_YEAR',
      defaultMessage: 'Emprove® Suite Subscription, 1 year',
    },
    productNumber: EmproveSubscriptions.ONE_YEAR,
  },
  {
    term: 2,
    name: {
      id: 'EMPROVE_SUBSCRIPTION_TWO_YEAR',
      defaultMessage: 'Emprove® Suite Subscription, 2 years',
    },
    productNumber: EmproveSubscriptions.TWO_YEAR,
  },
  {
    term: 5,
    name: {
      id: 'EMPROVE_SUBSCRIPTION_FIVE_YEAR',
      defaultMessage: 'Emprove® Suite Subscription, 5 years',
    },
    productNumber: EmproveSubscriptions.FIVE_YEAR,
  },
]

const SubscriptionAddToCart = ({ handleClose }) => {
  const classes = useStyles()
  const [subscriptionSelection, setSubscriptionSelection] = useState(
    subscriptionOptions[0].productNumber
  )
  const router = useRouter()
  const { catalog = '' } = router.query || {}
  const [addToCart] = useAddToCart()
  const { setSnackbar } = useGlobalSnackbar()
  const [submitting, setSubmitting] = React.useState(false)
  const sendOptlyAddToCart = useOptlyAddToCart()

  const { data, loading, error } = usePricingAndAvailabilityQuery({
    variables: {
      productNumber: subscriptionSelection,
      brand: 'SIGALD',
      quantity: 1,
      catalogType: determineCatalogType(catalog),
    },
  })

  const handleAddToCart = async () => {
    setSubmitting(true)
    try {
      const option = subscriptionOptions.find(
        (option) => option.productNumber === subscriptionSelection
      )
      await addToCart({
        variables: {
          input: {
            items: [
              {
                materialNumber: subscriptionSelection,
                quantity: 1,
                type: CartItemType.Emprove,
                materialDescription: `${option?.name?.defaultMessage}`,
                emproveDossierInfo: {
                  dossierId: 'NA',
                  dossierSubType: DossierSubType.All,
                  dossierSubLength: option?.term,
                  dossierMaterialNumber: 'Subscription',
                },
              },
            ],
          },
        },
        refetchQueries: [
          {
            query: QUICK_CART,
            variables: { id: CartRequestAction.Mini },
          },
        ],
        awaitRefetchQueries: true,
      })
      setSubmitting(false)
      handleClose()
      setSnackbar(SnackbarMessages.ItemsAddedToCart, 1)
      const pricing = data?.getPricingForProduct?.materialPricing[0]
      setEmproveAddToCartData(
        [
          {
            materialNumber: subscriptionSelection,
            materialDescription: `${option?.name?.defaultMessage}`,
            dossierMaterialNumber: 'Subscription',
            pricing: pricing?.price,
            currency: pricing?.currency,
          },
        ],
        AddToCartPagesEnum.EmproveProducts
      )
      sendOptlyAddToCart(`emprove_${subscriptionSelection}`)
    } catch (err) {
      setSubmitting(false)
      const { hasError } = extractData(err as OriginalError)
      let errorMessage = ''
      if (hasError('CANNOT_CONTAIN_OTHER_PRODUCT_TYPE_WITH_EMPROVE_PRODUCTS')) {
        errorMessage = SnackbarMessages.CartMixingError
      } else if (
        hasError('CANNOT_CONTAIN_SAME_DOSSIER_WITH_EMPROVE_PRODUCTS')
      ) {
        errorMessage = SnackbarMessages.SameEmproveError
      } else {
        errorMessage = SnackbarMessages.GenericError
      }

      setSnackbar(errorMessage)
    }
  }

  return (
    <Grid>
      <Grid container className={classes.documentPurchase}>
        <Grid item className={classes.imageContainer}>
          <CatalogImage image={{}} className={classes.defaultImage} />
        </Grid>
        <Grid item className={classes.documentDetails}>
          <Grid container direction="column">
            <Grid item>
              <Box fontWeight="fontWeightMedium" pb={2}>
                <FormattedMessage
                  id="PURCHASE_EMPROVE_SUITE"
                  defaultMessage="Purchase The Emprove® Suite"
                />
              </Box>
            </Grid>
            <Grid item className={classes.purchaseTerms}>
              <FormattedMessage
                id="SUBSCRIPTION_PURCHASE_TERMS"
                defaultMessage="A subscription grants access to documents for every single Emprove® Program product."
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid className={classes.cartDetails}>
        <FormControl className={classes.formControl}>
          <InputLabel shrink id="emprove-susbcription-selection-label">
            <FormattedMessage
              id="SUBSCRIPTION_DURATION"
              defaultMessage="Subscription Duration"
            />
          </InputLabel>
          <Select
            labelId="emprove-susbcription-selection-label"
            id="emprove-subscription-selection"
            value={subscriptionSelection}
            onChange={(e) =>
              setSubscriptionSelection(e.target.value as EmproveSubscriptions)
            }
            className={classes.selectEmpty}
          >
            {subscriptionOptions.map((subscription) => (
              <MenuItem
                key={subscription.productNumber}
                value={subscription.productNumber}
              >
                <FormattedMessage {...subscription.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box fontWeight="fontWeightMedium">
          <EmprovePricing data={data} loading={loading} error={error} />
        </Box>
        <AddToCartButton
          handleAddToCart={handleAddToCart}
          dirty
          isSubmitting={submitting}
          canAddToCart
          hideAddToCartForPrepackItems={false}
          disabled={error ? true : false}
        />
      </Grid>
    </Grid>
  )
}

export default SubscriptionAddToCart
